<template>
  <div class="p-6">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">佣金记录</h1>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">查看所有推广佣金结算记录</p>
    </div>

    <!-- 列表 -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900 overflow-x-auto">
      <table class="w-full text-sm text-left">
        <thead class="text-xs text-gray-500 dark:text-gray-400 uppercase bg-gray-50 dark:bg-gray-700">
          <tr>
            <th class="px-4 py-3">ID</th>
            <th class="px-4 py-3">来源用户</th>
            <th class="px-4 py-3">层级</th>
            <th class="px-4 py-3">订单类型</th>
            <th class="px-4 py-3">订单金额</th>
            <th class="px-4 py-3">费率</th>
            <th class="px-4 py-3">佣金金额</th>
            <th class="px-4 py-3">状态</th>
            <th class="px-4 py-3">时间</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="9" class="px-4 py-8 text-center text-gray-400 dark:text-gray-500">加载中...</td>
          </tr>
          <tr v-else-if="records.length === 0">
            <td colspan="9" class="px-4 py-8 text-center text-gray-400 dark:text-gray-500">暂无佣金记录</td>
          </tr>
          <tr v-for="r in records" :key="r.id" class="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
            <td class="px-4 py-3 dark:text-gray-300">{{ r.id }}</td>
            <td class="px-4 py-3 dark:text-gray-300">{{ r.sourceUsername || r.sourceUserId }}</td>
            <td class="px-4 py-3 dark:text-gray-300">L{{ r.level }}</td>
            <td class="px-4 py-3 dark:text-gray-300">{{ r.orderType }}</td>
            <td class="px-4 py-3 dark:text-gray-300">¥{{ centsToYuan(r.orderAmount) }}</td>
            <td class="px-4 py-3 dark:text-gray-300">{{ (r.rate * 100).toFixed(0) }}%</td>
            <td class="px-4 py-3 font-medium text-green-600 dark:text-green-400">+¥{{ centsToYuan(r.commissionAmount) }}</td>
            <td class="px-4 py-3">
              <span :class="r.status === 'SETTLED'
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : r.status === 'PENDING'
                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'"
                class="px-2 py-1 text-xs rounded-full">
                {{ r.status === 'SETTLED' ? '已结算' : r.status === 'PENDING' ? '待解锁' : '已退款' }}
              </span>
            </td>
            <td class="px-4 py-3 text-gray-500 dark:text-gray-400">{{ formatDate(r.createdAt) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 分页 -->
    <Pagination v-if="total > 0"
      :total="total"
      v-model:current-page="currentPage"
      v-model:page-size="pageSize"
      :show-page-size-selector="false"
      :show-jumper="false"
      @update:current-page="loadRecords"
      @update:page-size="loadRecords"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { getCommissionsAPI } from '@/api/referral/commission'
import Pagination from '@/components/ui/Pagination.vue'
import type { CommissionRecord } from '@/types/referral/commission'

const PAGE_SIZE = 20

const records = ref<CommissionRecord[]>([])
const total = ref(0)
const currentPage = ref(1)
const pageSize = ref(PAGE_SIZE)
const loading = ref(false)

/** 分转元 */
const centsToYuan = (cents: number) => (cents / 100).toFixed(2)

const formatDate = (dateStr: string) => new Date(dateStr).toLocaleString()

const loadRecords = async () => {
  loading.value = true
  try {
    const res = await getCommissionsAPI(currentPage.value, pageSize.value)
    if (res.status === 200 && res.data) {
      records.value = res.data.items
      total.value = res.data.total
    }
  } catch (error) {
    console.error('加载佣金记录失败:', error)
  } finally {
    loading.value = false
  }
}

onMounted(() => loadRecords())
</script>
